/* eslint-disable consistent-return */
import * as React from 'react';
import dayjs from 'dayjs';
import { FormikValues, FormikTouched } from 'formik';
import SimpleSelect from '../components/Forms/Fields/SimpleSelect';
import SimpleSelectMultiple from '../components/Forms/Fields/SimpleSelectMultiple';
import Input from '../components/Forms/Fields/Input';
import Textarea from '../components/Forms/Fields/Textarea';
import { IInput, IValidationRule } from './FormikValidatorService';
import { IFormValues } from '../interfaces/form';
import GroupSelect from '../components/Forms/Fields/GroupSelect';
import RadioInput from '../components/Forms/Fields/RadioInput';
import { ISimpleSelectOption } from '../components/Forms/ArrayFields/interfaces';
import { IVehicle } from '../components/Vehicles/types';

export const TYPE_GROUP_SELECT = 'groupSelect';
const VEHICLE_ID_FIELD = 'vehicleId';

type TOnChangeCallback = (values: IFormValues[]) => void;

const FormikService = {
  getInputTextField(item: IInput): JSX.Element {
    return (
      <Input
        id={item.indexId}
        key={`input-${item.indexId}`}
        data-cy={`input-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        type="text"
      />
    );
  },

  getLabelField(item: IInput): JSX.Element {
    let { value } = item;
    const regexFindColor = /#[0-9a-f]{6}|#[0-9a-f]{3}/gi;

    if (item.values) {
      if (value && Object.keys(value).length > 0) {
        const filteredItems = item.values.filter((i: any) => value.includes(i.value));
        if (filteredItems && filteredItems[0] && filteredItems[0].item) {
          value = filteredItems.map((i: any) => {
            return <div key={i.id}>{i.item}</div>;
          });
        }
      } else {
        const filteredItem = item.values.filter((i: any) => i.value === value);
        if (filteredItem && filteredItem[0] && filteredItem[0].item) {
          value = filteredItem[0].item;
        }
      }
    }

    if (item.subtype) {
      if (item.subtype === 'datetime') {
        value = dayjs(item.value).format('DD.MM.YYYY HH:mm');
      } else if (item.subtype === 'time') {
        value = dayjs(item.value).format('HH:mm');
      } else if (item.subtype === 'date') {
        value = dayjs(item.value).format('DD.MM.YYYY');
      }
    }

    if (value && Object.keys(value).length > 0 && value[0].fileName) {
      value.map((file: any) => {
        const thisFile = file;
        const { mimeType } = thisFile;
        const typeArr = mimeType.split('/');
        if (typeArr.length > 1 && typeArr[1] === 'pdf') {
          thisFile.cssType = '-pdf';
        } else if (typeArr[0] === 'application') {
          thisFile.cssType = '';
        } else {
          thisFile.cssType = `-${typeArr[0]}`;
        }
        return thisFile;
      });
      value = Object.values(value).map((file: any) => (
        <div className="label label-outline mb-xs py-xs" key={file.id}>
          <i className={`cmd-icons cmd-icon-file${file.cssType} fs-4 mr-xs`} />
          <span className="position-relative bottom-1">{file.fileName}</span>
        </div>
      ));
    }

    if (regexFindColor.test(value)) {
      value = (
        <div
          style={{
            backgroundColor: value,
            height: 20,
            width: 20,
            borderRadius: 5,
            border: '1px #ccc solid',
          }}
        />
      );
    }

    return item.title ? (
      <div className="row form-group">
        <div className="text-left font-weight-bold col-sm-3 control-label">{item.title}</div>
        <div className="col-sm-9" style={{ paddingTop: '6px' }}>
          {value}
        </div>
      </div>
    ) : (
      <></>
    );
  },

  getInputPasswordField(item: IInput): JSX.Element {
    return (
      <Input
        id={item.indexId}
        key={`input-${item.indexId}`}
        data-cy={`input-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        type="password"
      />
    );
  },

  getInputColorField(item: IInput): JSX.Element {
    return (
      <Input
        id={item.indexId}
        key={`input-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        type="color"
      />
    );
  },

  getInputRadioField(item: IInput): JSX.Element {
    return (
      <RadioInput
        id={item.indexId}
        key={`input-radio-${item.indexId}`}
        disabled={item.readonly}
        name={item.indexId}
        label={item.title}
        options={item.values}
        rowOrRows="row"
      />
    );
  },

  getInputRangeField(item: IInput): JSX.Element {
    return (
      <Input
        id={item.indexId}
        key={`input-range-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        type="number"
        placeholder="form.numberValue"
      />
    );
  },

  getHiddenField(item: IInput): JSX.Element {
    return (
      <Input
        id={item.indexId}
        key={`input-hidden-${item.indexId}`}
        name={item.indexId}
        type="hidden"
        label=""
      />
    );
  },

  getInputNumberField(item: IInput): JSX.Element {
    return (
      <Input
        id={item.indexId}
        key={`input-number-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        type="number"
        placeholder={item.placeholder}
      />
    );
  },

  getTextareaField(item: IInput, rows = 5): JSX.Element {
    return (
      <Textarea
        id={item.indexId}
        key={`textarea-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        rows={rows}
        // value={item.value ? item.value : ''}
      />
    );
  },

  getUploadField(item: IInput): JSX.Element {
    return <p>No upload component defined.</p>;
  },

  getGroupSelectField(item: IInput): JSX.Element {
    return (
      <GroupSelect
        id={item.indexId}
        key={`group-select-${item.indexId}`}
        data-cy={`group-select-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        options={item.values.length === 0 ? {} : item.values}
        placeholder={item.placeholder.length === 0 ? '' : item.placeholder}
        disabled={item.disabled}
      />
    );
  },

  getSimpleSelectField(
    item: IInput,
    selectedVehicles: number[],
    vehicles: IVehicle[],
    onChangeCallback: TOnChangeCallback | undefined = undefined,
    isNavigatorDisabled = false
  ): JSX.Element {
    let vehicleValues: ISimpleSelectOption[] = [];
    if (item.indexId === VEHICLE_ID_FIELD && !isNavigatorDisabled) {
      const choiceInListIndex = item.rules.findIndex(
        (rule: IValidationRule) => rule.param === 'choiseInList'
      );
      if (choiceInListIndex > -1) {
        item.rules[choiceInListIndex].value = selectedVehicles;
      }
      vehicleValues = vehicles
        .filter((vehicle: IVehicle) => selectedVehicles.includes(vehicle.id))
        .map((vehicle: IVehicle) => {
          return {
            value: String(vehicle.id),
            item: `${vehicle.licenceNumber} ${vehicle.name}`,
          };
        });
    }

    if (item.multiple) {
      return (
        <SimpleSelectMultiple
          id={item.indexId}
          key={`simple-select-multiple-${item.indexId}`}
          name={item.indexId}
          label={item.title}
          options={item.values}
          placeholder="form.chosen"
          searchPlaceholder="form.search"
        />
      );
    }

    return (
      <SimpleSelect
        id={`simple-select-${item.indexId}`}
        key={`simple-select-${item.indexId}`}
        data-cy={`simple-select-${item.indexId}`}
        name={item.indexId}
        label={item.title}
        options={
          item.indexId === VEHICLE_ID_FIELD && !isNavigatorDisabled ? vehicleValues : item.values
        }
        introOption={item.placeholder ? item.placeholder : 'form.choose'}
        introDisabled={item.firstDisabled}
        disabled={item.readonly}
        // onChange={item.sendOnChange ? onChangeCallback : undefined}
      />
    );
  },

  setFieldsTouched(
    values: FormikValues,
    setTouched: (touched: FormikTouched<any>, shouldValidate?: boolean | undefined) => void
  ): void {
    const fields = Object.keys(values);
    const touchedFields: FormikTouched<any> = {};
    if (fields) {
      fields.map((key: string) => {
        touchedFields[key] = true;
      });
    }
    setTouched(touchedFields);
  },

  toBoolean(o: FormikValues): FormikValues {
    Object.keys(o).forEach((k: any) => {
      if (typeof o[k] === 'object') {
        return this.toBoolean(o[k]);
      }
      o[k] = true;
    });
    return o;
  },

  setInitialValues(formData: IInput[]): FormikValues {
    const initialValues: FormikValues = {};
    formData.map((item: IInput) => {
      if (item.type === 'select' && item.multiple && !item.value) {
        initialValues[item.indexId] = [];
      } else {
        initialValues[item.indexId] = item.value;
      }
    });
    return initialValues;
  },

  getOptionByValue(options: ISimpleSelectOption[], optionValue: string): string | null {
    const option = options.find(
      (item: ISimpleSelectOption) => String(item.value) === String(optionValue)
    );
    if (option) {
      return option.item;
    }
    return null;
  },
};

export default FormikService;
